import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import translations from './welcome.i18n';
import { Link, NavLink } from 'react-router-dom';
import classNames from 'classnames';
import Version from '../components/version/version';
import FlagWrapper, {
  FlagTypes,
  getFlagFromLanguage,
  getLanguageFromFlagType,
} from '../components/flags/flagcomponents';
import { LanguageContext } from '../i18n/LanguageStore';

interface LinksProps {
  css: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}
export const WhiteLinks: React.FC<React.PropsWithChildren<LinksProps>> = ({
  css,
}) => {
  const { formatMessage } = useIntl();
  const labels = {
    rightsAndPrivacy: formatMessage({ id: translations.rightsAndPrivacy.id }),
    omTjenesten: formatMessage({ id: translations.omTjenesten.id }),
  };

  return (
    <div className={css.whiteLinkWrapper}>
      <NavLink
        className={({ isActive }) =>
          classNames(css.whiteLink, isActive && css.current)
        }
        to="/about"
        data-cy={'linkAbout'}
      >
        {labels.rightsAndPrivacy}
      </NavLink>
      <Link
        className={css.whiteLink}
        to="https://sikt.no/tjenester/arbeidslivsportalen"
        data-cy={'omTjenesten'}
      >
        {labels.omTjenesten}
      </Link>
    </div>
  );
};

export const BottomLinks: React.FC<React.PropsWithChildren<LinksProps>> = ({
  css,
}) => {
  const { formatMessage } = useIntl();
  const labels = {
    rightsAndPrivacy: formatMessage({ id: translations.rightsAndPrivacy.id }),
    cookies: formatMessage({ id: translations.aboutCookiesTitle.id }),
    analytics: formatMessage({ id: translations.aboutAnalyticsTitle.id }),
    accessibility: formatMessage({
      id: translations.accessibilityStatement.id,
    }),
  };
  const { selectedLanguage, setSelectedLanguage } = useContext(LanguageContext);

  const setLanguage = (flag: FlagTypes) => {
    setSelectedLanguage(getLanguageFromFlagType(flag));
  };

  return (
    <nav className={css.bottomLinks}>
      <Link className={css.bottomLink} to="/about" data-cy={'bottomLinkAbout'}>
        {labels.rightsAndPrivacy}
      </Link>
      <Link
        className={css.bottomLink}
        to="/cookies"
        data-cy={'bottomLinkCookies'}
      >
        {labels.cookies}
      </Link>
      <Link
        className={css.bottomLink}
        to="/analytics"
        data-cy={'bottomLinkAnalytics'}
      >
        {labels.analytics}
      </Link>
      <Link
        className={css.bottomLink}
        to="https://uustatus.no/nb/erklaringer/publisert/8cc9ad7d-3bc0-4f9e-bbb1-95281870bf41"
      >
        {labels.accessibility}
      </Link>
      <div className={css.versionAndFlags}>
        <Version className={css.version} />
        <FlagWrapper
          flags={[FlagTypes.NO, FlagTypes.UK]}
          onChange={(event, value: FlagTypes) => setLanguage(value)}
          highlighted={getFlagFromLanguage(selectedLanguage)}
          label={'language-select'}
        />
      </div>
    </nav>
  );
};

interface PageProps {
  css: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  title: string;
  description: string;
  loggedOutMessage?: string;
  noTitleMargin?: boolean;
}
export const SmallPageTop: React.FC<React.PropsWithChildren<PageProps>> = ({
  css,
  title,
  description,
  loggedOutMessage,
}) => {
  return (
    <header>
      <div className={css.banner}>
        <div className={css.space24} />
        <div className={css.whiteLinks}>
          <WhiteLinks css={css} />
        </div>
        {loggedOutMessage && (
          <p className={css.infoLoggedOut} role="alert">
            {loggedOutMessage}
          </p>
        )}
      </div>
      <div className={css.contentContainer}>
        <div className={css.titleWrapper}>
          <MainTitle title={title} css={css} noTitleMargin />
        </div>

        {description && (
          <>
            <div className={css.space50} />
            <p
              className={css.description}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            <div className={css.space50} />
          </>
        )}
      </div>
    </header>
  );
};

export const DesktopPageTop: React.FC<React.PropsWithChildren<PageProps>> = ({
  css,
  title,
  description,
  loggedOutMessage,
  noTitleMargin,
}) => {
  return (
    <header>
      <div className={css.space50} />
      {loggedOutMessage && (
        <h1 className={css.infoLoggedOut} role="alert">
          {loggedOutMessage}
        </h1>
      )}

      <div className={css.whiteLinks}>
        <WhiteLinks css={css} />
      </div>

      <div className={css.contentContainer}>
        <MainTitle title={title} css={css} noTitleMargin={noTitleMargin} />
        <div className={css.space50} />
        <p
          className={css.description}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
    </header>
  );
};

interface MainTitleProps {
  title: string;
  css: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  noTitleMargin?: boolean;
}

export const MainTitle: React.FC<React.PropsWithChildren<MainTitleProps>> = ({
  css,
  title,
  noTitleMargin,
}) => (
  <Link
    to="/"
    className={classNames(css.title, noTitleMargin && css.noTitleMargin)}
    data-cy={'linkTitle'}
  >
    {title}
  </Link>
);
